// Step 4: Account Activation (Post-Payment)
// Security: Activation happens via webhook after payment confirmation
// UX: Success state with clear next steps

import React, { useEffect, useState } from 'react';
import { SubscriptionTier } from '../../config/subscriptions';

interface ActivationStepProps {
  user: any;
  selectedTier: SubscriptionTier;
  paymentStatus: 'pending' | 'processing' | 'completed' | 'failed';
}

const ActivationStep: React.FC<ActivationStepProps> = ({
  user,
  selectedTier,
  paymentStatus
}) => {
  const [activationStatus, setActivationStatus] = useState<'checking' | 'active' | 'pending' | 'failed'>('checking');

  useEffect(() => {
    // Check activation status
    const checkActivation = async () => {
      try {
        // In a real implementation, you'd check the user's current status
        // For now, we'll simulate based on payment status
        if (paymentStatus === 'completed') {
          // Simulate webhook processing time
          setTimeout(() => {
            setActivationStatus('active');
          }, 2000);
        } else {
          setActivationStatus('pending');
        }
      } catch (error) {
        console.error('Failed to check activation status:', error);
        setActivationStatus('failed');
      }
    };

    checkActivation();
  }, [paymentStatus]);

  // Auto-redirect to dashboard after successful activation
  useEffect(() => {
    if (activationStatus === 'active') {
      const timer = setTimeout(() => {
        window.location.href = '/dashboard';
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [activationStatus]);

  if (activationStatus === 'checking') {
    return (
      <div className="p-8">
        <div className="max-w-md mx-auto text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Activating Your Account...
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Please wait while we process your payment and activate your subscription.
            This usually takes just a few seconds.
          </p>
        </div>
      </div>
    );
  }

  if (activationStatus === 'failed') {
    return (
      <div className="p-8">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Activation Failed
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We encountered an issue activating your account. Your payment was processed successfully,
            but there was a technical issue with account activation.
          </p>
          <div className="space-y-3">
            <a
              href="mailto:support@eightythreelabs.com"
              className="block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Contact Support
            </a>
            <button
              onClick={() => window.location.reload()}
              className="block w-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (activationStatus === 'pending') {
    return (
      <div className="p-8">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L5.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Payment Processing
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Your payment is being processed. Account activation is pending payment confirmation.
            This usually takes just a few minutes.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              We'll send you an email confirmation once your account is fully activated.
              You can also refresh this page to check for updates.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="p-8">
      <div className="max-w-lg mx-auto text-center">
        
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        {/* Welcome Message */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          🎉 Welcome to EightyThreeLabs!
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
          Your {selectedTier.name} subscription has been activated successfully.
        </p>

        {/* Account Details */}
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 mb-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-green-700 dark:text-green-300">Account:</span>
              <span className="font-semibold text-green-900 dark:text-green-100">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-green-700 dark:text-green-300">Plan:</span>
              <span className="font-semibold text-green-900 dark:text-green-100">{selectedTier.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-green-700 dark:text-green-300">Billing:</span>
              <span className="font-semibold text-green-900 dark:text-green-100">{selectedTier.priceDisplay}/year</span>
            </div>
            <div className="flex items-center justify-between border-t border-green-200 dark:border-green-700 pt-4">
              <span className="text-green-700 dark:text-green-300">Status:</span>
              <span className="font-bold text-green-600 dark:text-green-400 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Active
              </span>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-4">
            🚀 What's Next?
          </h3>
          <div className="text-blue-800 dark:text-blue-200 text-sm space-y-2 text-left">
            <div className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1">•</span>
              <span>Explore our interactive math problems and learning paths</span>
            </div>
            <div className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1">•</span>
              <span>Set up your personalized learning goals</span>
            </div>
            <div className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1">•</span>
              <span>Check your email for additional resources and tips</span>
            </div>
            <div className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1">•</span>
              <span>Join our community for help and discussions</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <a
            href="/dashboard"
            className="block w-full bg-green-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors text-lg"
          >
            Go to Dashboard
          </a>
          <a
            href="/learn"
            className="block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Start Learning
          </a>
        </div>

        {/* Auto-redirect Notice */}
        <div className="mt-8 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center justify-center">
            <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
            <span>Redirecting to dashboard in 5 seconds...</span>
          </div>
        </div>

        {/* Support Link */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Need help getting started?{' '}
            <a href="mailto:support@eightythreelabs.com" className="text-blue-600 dark:text-blue-400 hover:underline">
              Contact Support
            </a>
          </p>
        </div>

      </div>
    </div>
  );
};

export default ActivationStep;